// ADR 24.9.26l §2/§3 — every route has a title, and a navigation says it.
//
// The route table owns the title key (`handle: { titleKey }` on each leaf in
// `router.tsx`); a page may override it with `useDocumentTitle`. This is the
// one writer of `document.title`, and the polite live region that speaks the
// composed title once per pathname change.
//
// Mounted in `Shell` beside `AdaptiveShell`, outside it, so the
// shell's slot indices are untouched. `useMatches` is a data-router hook, and
// `Shell` is only ever mounted under one.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useMatches } from 'react-router-dom'

import type { PaneLayout, WindowSizeClass } from '../layout/useWindowClass'
import type { AppSlot } from '../wasm'
import { getTitleOverride, subscribeTitleOverride } from './documentTitle'
import { useRouteFocus } from './useRouteFocus'

/**
 * ADR 6.10.26l D2/D5 — the header's Apps icon. `slot` orders the entries; `drawnAt`
 * names the layouts at which the page already draws that slot, so the icon hides
 * there. It is keyed by the shell's own unions (`drawnAt.paneLayout.list_detail`),
 * never a quoted slug, which 6.9.26p AC6 bans outside the classifier.
 */
export interface RouteAppsHandle {
  slot?: AppSlot
  drawnAt?: {
    paneLayout?: Partial<Record<PaneLayout, true>>
    sizeClass?: Partial<Record<WindowSizeClass, true>>
  }
}

/** What a leaf route carries in `handle`. */
export interface RouteTitleHandle {
  titleKey: string
  /** ADR 28.9.26e §7 — where the header's back arrow goes; absent means no arrow. */
  backTo?: string
  /** ADR 6.10.26l D5 — present on the four main routes; absent means no Apps icon. */
  apps?: RouteAppsHandle
}

function titleKeyOf(handle: unknown): string | null {
  if (typeof handle !== 'object' || handle === null) return null
  const key = (handle as Partial<RouteTitleHandle>).titleKey
  return typeof key === 'string' ? key : null
}

/**
 * The deepest matched route's title key — the leaf's, since guards carry none.
 * ADR 28.9.26e §7 — exported for the header, whose `<h1>` is the route's title.
 */
export function useRouteTitleKey(): string | null {
  const matches = useMatches()
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const key = titleKeyOf(matches[i].handle)
    if (key !== null) return key
  }
  return null
}

/** ADR 28.9.26e §7 — the leaf's `backTo`, read the way its title is. */
export function useRouteBackTo(): string | null {
  const matches = useMatches()
  const handle = matches[matches.length - 1]?.handle
  if (typeof handle !== 'object' || handle === null) return null
  const to = (handle as Partial<RouteTitleHandle>).backTo
  return typeof to === 'string' ? to : null
}

/** ADR 6.10.26l D5 — the leaf's `apps`, read the way its `backTo` is. */
export function useRouteApps(): RouteAppsHandle | null {
  const matches = useMatches()
  const handle = matches[matches.length - 1]?.handle
  if (typeof handle !== 'object' || handle === null) return null
  const apps = (handle as Partial<RouteTitleHandle>).apps
  return typeof apps === 'object' && apps !== null ? apps : null
}

/** `index.html`'s static `<title>`: what a route with no key (the index redirect) keeps. */
const BRAND = 'Lingara'

/**
 * The composed document title for a page title: a key, not a template literal,
 * because the brand's position and the separator are a translator's decision.
 */
function useComposeTitle(): (page: string | null) => string {
  const { t } = useTranslation()
  return useCallback(
    (page: string | null) => (page === null ? BRAND : t('page_title_format', { 0: page })),
    [t],
  )
}

/**
 * Clear, then set on the next frame, so a repeat of the same title is still a
 * change the screen reader hears.
 */
function useAnnouncement(): [string, (text: string) => void] {
  const [message, setMessage] = useState('')
  const frame = useRef<number | null>(null)
  useEffect(() => () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current)
  }, [])
  const announce = useCallback((text: string) => {
    setMessage('')
    if (frame.current !== null) cancelAnimationFrame(frame.current)
    frame.current = requestAnimationFrame(() => {
      frame.current = null
      setMessage(text)
    })
  }, [])
  return [message, announce]
}

export function RouteAnnouncer() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const titleKey = useRouteTitleKey()
  const override = useSyncExternalStore(subscribeTitleOverride, getTitleOverride)
  const compose = useComposeTitle()
  const routeTitle = titleKey === null ? null : t(titleKey)
  const title = compose(override ?? routeTitle)
  const [message, announce] = useAnnouncement()

  useEffect(() => {
    document.title = title
  }, [title])

  // The store is read at effect time, not render time: the leaving page's
  // cleanup and the arriving page's override both ran (child effects first)
  // before this one, and the render's `override` may still be the old page's.
  // A later override (a title that arrives after a fetch) is not re-announced.
  const onArrive = useCallback(
    () => announce(compose(getTitleOverride() ?? routeTitle)),
    [announce, compose, routeTitle],
  )
  useRouteFocus(pathname, onArrive)

  return (
    <p className="sr-only" aria-live="polite" aria-atomic="true" data-testid="route-announcer">
      {message}
    </p>
  )
}
